/**
 * Store Class Repository
 * Handles all store class-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'

export interface StoreClass {
    id: number
    tenant_id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
}

export interface CreateStoreClassRequest {
    tenant_id: number
    en_name: string
    ar_name?: string
    status?: number
    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateStoreClassRequest {
    tenant_id?: number
    en_name?: string
    ar_name?: string
    status?: number
    // Note: updated_by is automatically set by backend from logged-in user
}

class StoreClassRepository {
    /**
     * Get all store classes with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<StoreClass>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.store_class_id) {
                params.append('store_class_id', filters.store_class_id)
            }
        }

        return apiClient.get<PaginatedResponse<StoreClass>>(`/store-classes?${params.toString()}`)
    }

    /**
     * Get store class by ID
     */
    async getById(id: number): Promise<StoreClass> {
        return apiClient.get<StoreClass>(`/store-classes/${id}`)
    }

    /**
     * Create a new store class
     */
    async create(data: CreateStoreClassRequest): Promise<StoreClass> {
        return apiClient.post<StoreClass>('/store-classes', data)
    }

    /**
     * Update an existing store class
     */
    async update(id: number, data: UpdateStoreClassRequest): Promise<StoreClass> {
        return apiClient.put<StoreClass>(`/store-classes/${id}`, data)
    }

    /**
     * Delete a store class
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/store-classes/${id}`)
    }

    /**
     * Export store classes to Excel
     */
    async exportToExcel(filters?: any): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }

        if (filters?.store_class_id) {
            params.append('store_class_id', filters.store_class_id.toString())
        }

        const baseUrl = `/store-classes/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;


        return apiClient.getBlob(url, true, { showSuccessToast: true, successMessage: 'Exported Successfully' })
    }
}

// Export singleton instance
export const storeClassRepository = new StoreClassRepository()

